import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../utils/useAuth";
import { isAdmin, isSuperAdmin } from "../../../utils/permissions";
import { useSelector } from "react-redux";
import axios from "axios";
import AppModal from "../AppModal/AppModal";
import "../AddUserModal/AddUserModal.scss";
import "./UserProfileModal.scss";
import { BASE_API_URL } from "../../../utils/consts";

type Props = {
  isOpen: boolean;
  onCloseModal: () => void;
  userDetails: any;
  onUpdateProfile: (profileData: any) => void;
  locations: any;
};

const COLORS = [
  "oklch(0.6 0.15 25)",
  "oklch(0.7 0.14 70)",
  "oklch(0.62 0.14 150)",
  "oklch(0.55 0.16 265)",
  "oklch(0.55 0.16 320)",
  "oklch(0.5 0.02 260)",
  "oklch(0.65 0.15 195)",
  "oklch(0.48 0.2 280)",
  "oklch(0.68 0.12 95)",
  "oklch(0.58 0.18 10)",
  "oklch(0.62 0.13 230)",
];

const toDateInput = (val: any): string => {
  if (!val) return "";
  const d = new Date(val);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const getRoleName = (role: any): string => {
  if (!role) return "user";
  const name = typeof role === "string" ? role : (role.name ?? "");
  return name.replace(/^ROLE_?/i, "").toLowerCase();
};

const PersonIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5 20c0-3.6 3.1-5.6 7-5.6s7 2 7 5.6" />
  </svg>
);

const ChevDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const UserProfileModal: React.FC<Props> = ({
  isOpen,
  onCloseModal,
  onUpdateProfile,
  userDetails,
  locations,
}) => {
  const { t } = useTranslation();
  const authUser: any = useAuth();
  const [positions, setPositions] = useState<any[]>([]);

  const workingPatterns = useSelector((state: any) => state.workingPattern.contracts);
  const clientLeavesList = useSelector((state: any) => state.clientLeaves.clientLeaves);
  const clientOvertimes = useSelector((state: any) => state.clientLeaves.clientOvertimes);

  const isAdminUser = isAdmin(authUser?.roles);
  const isSuperAdminUser = isSuperAdmin(authUser?.roles);
  const isSelf = userDetails?.user?._id === authUser?.id;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    gender: "",
    birthday: "",
    role: "user",
    location: "",
    assignedLocationId: "",
    workingPatternId: "",
    positionInSector: "",
    clientLeaves: [] as string[],
    overtimeTypes: [] as string[],
    contractStart: "",
    contractEnd: "",
    sanitaryBookletIssuedAt: "",
    sanitaryBookletValidUntil: "",
    color: COLORS[3],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (userDetails?.user?.clientId && isOpen) {
      const cid = userDetails.user.clientId?._id ?? userDetails.user.clientId;
      axios
        .get(`${BASE_API_URL}/admin/${cid}/getPositions`)
        .then((res) => setPositions(res.data))
        .catch(() => {});
    }
  }, [isOpen, userDetails?.user?.clientId]);

  useEffect(() => {
    if (!isOpen || !userDetails?.user) return;
    const u = userDetails.user;
    setFormData({
      firstName: u.firstName ?? "",
      lastName: u.lastName ?? "",
      phoneNumber: u.phoneNumber ?? "",
      gender: u.gender ?? "",
      birthday: toDateInput(u.birthday),
      role: getRoleName(u.roles?.[0]),
      location: u.location?._id ?? u.location ?? "",
      assignedLocationId: u.assignedLocationId?._id ?? u.assignedLocationId ?? "",
      workingPatternId: u.workingPatternId?._id ?? u.workingPatternId ?? "",
      positionInSector: u.positionInSector?.[0]?._id ?? u.positionInSector?.[0] ?? "",
      clientLeaves: u.clientLeaves?.map((l: any) => l._id ?? l) ?? [],
      overtimeTypes: u.overtimeTypes?.map((o: any) => o._id ?? o) ?? [],
      contractStart: toDateInput(u.contractStart),
      contractEnd: toDateInput(u.contractEnd),
      sanitaryBookletIssuedAt: toDateInput(u.sanitaryBookletIssuedAt),
      sanitaryBookletValidUntil: toDateInput(u.sanitaryBookletValidUntil),
      color: u.color ?? COLORS[3],
    });
    setErrors({});
  }, [isOpen, userDetails]);

  const setField = (key: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [key]: e.target.value,
      ...(key === "location" ? { positionInSector: "" } : {}),
    }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const toggleMulti = (key: "clientLeaves" | "overtimeTypes", id: string) => {
    setFormData((prev) => {
      const arr: string[] = prev[key];
      return {
        ...prev,
        [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id],
      };
    });
  };

  const filteredPositions = positions.filter(
    (pos: any) => pos?.workSector?.location?._id?.toString() === formData.location
  );

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.firstName.trim()) e.firstName = "Ime je obavezno";
    if (!formData.lastName.trim()) e.lastName = "Prezime je obavezno";
    if (!formData.gender) e.gender = "Pol je obavezan";
    return e;
  };

  const handleSubmit = () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    onUpdateProfile({
      ...formData,
      positionInSector: formData.positionInSector ? [formData.positionInSector] : [],
      contractEnd: formData.contractEnd || null,
      birthday: formData.birthday || null,
      sanitaryBookletIssuedAt: formData.sanitaryBookletIssuedAt || null,
      sanitaryBookletValidUntil: formData.sanitaryBookletValidUntil || null,
    });
  };

  const selectClass = (val: string, err?: string) =>
    `am-select${!val ? " am-placeholder" : ""}${err ? " am-input-error" : ""}`;

  return (
    <AppModal
      open={isOpen}
      onClose={onCloseModal}
      icon={<PersonIcon />}
      eyebrow="Uredi zaposlenog"
      title="Ažuriraj profil"
      description="Izmijenite podatke zaposlenog, pristup i ugovor."
      onSubmit={handleSubmit}
      submitLabel="Sačuvaj izmjene"
      maxWidth={680}
    >
      {/* ── Lični podaci ── */}
      <div className="am-section">
        <div className="am-section-label">Lični podaci</div>
        <div className="am-form-grid">

          <div className="am-field">
            <label className="am-label">Ime <span className="am-req">*</span></label>
            <input
              className={`am-input${errors.firstName ? " am-input-error" : ""}`}
              value={formData.firstName}
              onChange={setField("firstName")}
            />
            {errors.firstName && <span className="am-error-msg">{errors.firstName}</span>}
          </div>

          <div className="am-field">
            <label className="am-label">Prezime <span className="am-req">*</span></label>
            <input
              className={`am-input${errors.lastName ? " am-input-error" : ""}`}
              value={formData.lastName}
              onChange={setField("lastName")}
            />
            {errors.lastName && <span className="am-error-msg">{errors.lastName}</span>}
          </div>

          <div className="am-field">
            <label className="am-label">Telefon <span className="am-field-hint">opciono</span></label>
            <input
              className="am-input"
              placeholder="+387..."
              value={formData.phoneNumber}
              onChange={setField("phoneNumber")}
            />
          </div>

          <div className="am-field">
            <label className="am-label">Pol <span className="am-req">*</span></label>
            <div className="am-select-wrap">
              <select
                className={selectClass(formData.gender, errors.gender)}
                value={formData.gender}
                onChange={setField("gender")}
              >
                <option value="">Molimo izaberite</option>
                <option value="male">Muški</option>
                <option value="female">Ženski</option>
                <option value="other">Ne želim da navedem</option>
              </select>
              <span className="am-chev"><ChevDown /></span>
            </div>
            {errors.gender && <span className="am-error-msg">{errors.gender}</span>}
          </div>

          <div className="am-field">
            <label className="am-label">Datum rođenja <span className="am-field-hint">opciono</span></label>
            <input
              className="am-input am-date"
              type="date"
              value={formData.birthday}
              onChange={setField("birthday")}
            />
          </div>
        </div>
      </div>

      {/* ── Nalog i pristup ── */}
      <div className="am-section">
        <div className="am-section-label">Nalog i pristup</div>
        <div className="am-form-grid">

          {(isAdminUser || isSuperAdminUser) && (
            <div className="am-field">
              <label className="am-label">Uloga</label>
              <div className="am-select-wrap">
                <select
                  className={selectClass(formData.role)}
                  value={formData.role}
                  disabled={isSelf}
                  onChange={setField("role")}
                >
                  <option value="user">Zaposleni</option>
                  <option value="moderator">Menadžer</option>
                  <option value="admin">Administrator</option>
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>
          )}

          <div className="am-field">
            <label className="am-label">Lokacija</label>
            <div className="am-select-wrap">
              <select
                className={selectClass(formData.location)}
                value={formData.location}
                onChange={setField("location")}
              >
                <option value="">Izaberi lokaciju</option>
                {locations?.map((loc: any) => (
                  <option key={loc._id} value={loc._id}>{loc.name}</option>
                ))}
              </select>
              <span className="am-chev"><ChevDown /></span>
            </div>
          </div>

          {isAdminUser && (
            <div className="am-field">
              <label className="am-label">
                Ograničenje lokacije
                <span className="am-field-hint">opciono</span>
              </label>
              <div className="am-select-wrap">
                <select
                  className={selectClass(formData.assignedLocationId)}
                  value={formData.assignedLocationId}
                  onChange={setField("assignedLocationId")}
                >
                  <option value="">Bez ograničenja (sve lokacije)</option>
                  {locations?.map((loc: any) => (
                    <option key={loc._id} value={loc._id}>{loc.name}</option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Pozicija i ugovor ── */}
      {isAdminUser && (
        <div className="am-section">
          <div className="am-section-label">Pozicija i ugovor</div>
          <div className="am-form-grid">

            <div className="am-field">
              <label className="am-label">Tip ugovora</label>
              <div className="am-select-wrap">
                <select
                  className={selectClass(formData.workingPatternId)}
                  value={formData.workingPatternId}
                  onChange={setField("workingPatternId")}
                >
                  <option value="">Izaberite tip ugovora</option>
                  {workingPatterns?.map((wp: any) => (
                    <option key={wp._id} value={wp._id}>{wp.contractName}</option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>

            <div className="am-field">
              <label className="am-label">
                Pozicija u sektoru
                <span className="am-field-hint">opciono</span>
              </label>
              <div className="am-select-wrap">
                <select
                  className={selectClass(formData.positionInSector)}
                  value={formData.positionInSector}
                  onChange={setField("positionInSector")}
                >
                  <option value="">
                    {!formData.location ? "Prvo izaberite lokaciju…" : "Izaberite poziciju"}
                  </option>
                  {filteredPositions.map((pos: any) => (
                    <option key={pos._id} value={pos._id}>
                      {pos.name} ({pos.workSector?.name})
                    </option>
                  ))}
                </select>
                <span className="am-chev"><ChevDown /></span>
              </div>
            </div>

            <div className="am-field">
              <label className="am-label">Početak rada</label>
              <input
                className="am-input am-date"
                type="date"
                value={formData.contractStart}
                onChange={setField("contractStart")}
              />
            </div>

            <div className="am-field">
              <label className="am-label">
                Kraj ugovora
                <span className="am-field-hint">opciono</span>
              </label>
              <input
                className="am-input am-date"
                type="date"
                value={formData.contractEnd}
                onChange={setField("contractEnd")}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Sanitarna knjižica ── */}
      {isAdminUser && (
        <div className="am-section">
          <div className="am-section-label">Sanitarna knjižica</div>
          <div className="am-form-grid">
            <div className="am-field">
              <label className="am-label">Sanitarna izdata <span className="am-field-hint">opciono</span></label>
              <input
                className="am-input am-date"
                type="date"
                value={formData.sanitaryBookletIssuedAt}
                onChange={setField("sanitaryBookletIssuedAt")}
              />
            </div>
            <div className="am-field">
              <label className="am-label">Sanitarna važi do <span className="am-field-hint">opciono</span></label>
              <input
                className="am-input am-date"
                type="date"
                value={formData.sanitaryBookletValidUntil}
                onChange={setField("sanitaryBookletValidUntil")}
              />
            </div>
          </div>
        </div>
      )}

      {/* ── Godišnji odmori ── */}
      {isAdminUser && clientLeavesList?.length > 0 && (
        <div className="am-section">
          <div className="am-section-label">Godišnji odmori</div>
          <div className="upm-check-list">
            {clientLeavesList.map((item: any) => (
              <label key={item._id} className="upm-check-item">
                <input
                  type="checkbox"
                  className="upm-checkbox"
                  checked={formData.clientLeaves.includes(item._id)}
                  onChange={() => toggleMulti("clientLeaves", item._id)}
                />
                <span>{item.leaveName}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* ── Prekovremeni rad ── */}
      {isAdminUser && clientOvertimes?.length > 0 && (
        <div className="am-section">
          <div className="am-section-label">Prekovremeni rad</div>
          <div className="upm-check-list">
            {clientOvertimes.map((item: any) => (
              <label key={item._id} className="upm-check-item">
                <input
                  type="checkbox"
                  className="upm-checkbox"
                  checked={formData.overtimeTypes.includes(item._id)}
                  onChange={() => toggleMulti("overtimeTypes", item._id)}
                />
                <span>{item.overtimeName}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* ── Oznaka / boja ── */}
      <div className="am-section">
        <div className="am-section-label">Oznaka</div>
        <div className="am-field">
          <label className="am-label">
            Boja zaposlenog
            <span className="am-field-hint">za kalendar i raspored</span>
          </label>
          <div className="aum-swatches">
            {COLORS.map((c) => (
              <span
                key={c}
                className={`aum-swatch${formData.color === c ? " sel" : ""}`}
                style={{ background: c }}
                onClick={() => setFormData((prev) => ({ ...prev, color: c }))}
              />
            ))}
            <label
              className={`aum-swatch aum-swatch-custom${!COLORS.includes(formData.color) ? " sel" : ""}`}
              style={!COLORS.includes(formData.color) ? { background: formData.color } : undefined}
              title="Prilagođena boja"
            >
              <input
                type="color"
                className="aum-color-input"
                value={!COLORS.includes(formData.color) ? formData.color : "#3b82f6"}
                onChange={(e) => setFormData((prev) => ({ ...prev, color: e.target.value }))}
              />
            </label>
          </div>
        </div>
      </div>
    </AppModal>
  );
};

export default UserProfileModal;
